import React, { useCallback, useEffect, useState } from 'react';

// 접속 기록 — "누가 로그인을 시도했나" 를 DB 안 보고 확인하는 화면.
// 2026-11-04, 사무실 IP 에서 비번을 잘못 쳐 10분 잠금에 걸렸는데 화면상으로는
// 그냥 "로그인 실패" 로만 보여 원인을 못 찾았던 일이 있어 만들었다.

const FAIL_LIMIT = 5;        // serve.js 와 같은 값
const FAIL_WINDOW_MIN = 10;

const fmt = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

const fmtDay = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const left = (expires) => {
  const ms = new Date(expires).getTime() - Date.now();
  if (!(ms > 0)) return '만료';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}시간 ${m}분` : `${m}분`;
};

function AdminAccess({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    setError('');
    fetch('/api/admin/access', { headers: { Authorization: 'Bearer ' + token } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('unauthorized'))))
      .then(setData)
      .catch(() => setError('기록을 불러오지 못했습니다.'));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const revoke = (s) => {
    const who = s.IP || '알 수 없는 주소';
    if (!window.confirm(`${who} 의 세션을 끊습니다.\n그 기기는 즉시 로그아웃됩니다.`)) return;
    setBusy(s.HANDLE);
    fetch('/api/admin/session/revoke', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ handle: s.HANDLE }),
    })
      .then((r) => r.json())
      .then(() => load())
      .catch(() => setError('끊지 못했습니다.'))
      .finally(() => setBusy(''));
  };

  if (error) {
    return (
      <div className="ad-card">
        <p className="admin-error">{error}</p>
        <button type="button" className="admin-btn-primary" onClick={load}>다시 시도</button>
      </div>
    );
  }
  if (!data) return <div className="ad-card"><p className="ad-card-sub">불러오는 중…</p></div>;

  const attempts = data.attempts || [];
  const byIp = data.byIp || [];
  const sessions = data.sessions || [];

  // 지금 잠겨 있는 IP — 최근 10분 실패가 한계 이상이면 그 주소는 비번이 맞아도 거부된다.
  const since = Date.now() - FAIL_WINDOW_MIN * 60 * 1000;
  const recentFails = {};
  attempts.forEach((a) => {
    if (a.SUCCESS) return;
    if (new Date(a.CREATED_AT).getTime() < since) return;
    recentFails[a.IP] = (recentFails[a.IP] || 0) + 1;
  });
  const locked = Object.keys(recentFails).filter((ip) => recentFails[ip] >= FAIL_LIMIT);

  // 한 번도 성공하지 못한 주소 = 남이 비번을 맞혀 보려 한 흔적
  const intruders = byIp.filter((r) => Number(r.OKCNT) === 0 && Number(r.NGCNT) > 0);

  return (
    <>
      <div className="ad-card">
        <h3 className="ad-card-h">
          지금 로그인된 기기 <span className="ad-hint">{sessions.length}개</span>
        </h3>
        <p className="ad-card-sub">
          로그인은 <b>12시간</b> 유지됩니다. 내 것이 아닌 주소가 보이면 끊고 비밀번호를 바꾸세요.
        </p>
        <div className="ad-table-wrap"><table className="admin-table">
          <thead>
            <tr><th>접속 주소</th><th>로그인 시각</th><th>남은 시간</th><th /></tr>
          </thead>
          <tbody>
            {sessions.length === 0 && <tr><td colSpan="4" className="ad-empty">없습니다.</td></tr>}
            {sessions.map((s) => (
              <tr key={s.HANDLE}>
                <td>
                  {s.IP || '-'}
                  {Number(s.MINE) === 1 && <span className="ad-tag">지금 이 창</span>}
                </td>
                <td>{fmt(s.CREATED_AT)}</td>
                <td>{left(s.EXPIRES_AT)}</td>
                <td>
                  {Number(s.MINE) === 1 ? (
                    <span className="ad-hint">—</span>
                  ) : (
                    <button type="button" className="admin-btn-sm"
                            disabled={busy === s.HANDLE} onClick={() => revoke(s)}>
                      {busy === s.HANDLE ? '끊는 중…' : '끊기'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {locked.length > 0 && (
        <div className="ad-card">
          <h3 className="ad-card-h">지금 잠긴 주소</h3>
          <p className="ad-card-sub">
            {FAIL_WINDOW_MIN}분 안에 {FAIL_LIMIT}번 틀리면 그 주소는 잠깁니다. 잠긴 동안에는
            <b> 비밀번호가 맞아도 거부됩니다</b> — 비번이 틀린 게 아니니 계속 시도하지 말고 기다리세요.
          </p>
          <ul className="ad-iplist">
            {locked.map((ip) => (
              <li key={ip}>{ip} — 최근 {FAIL_WINDOW_MIN}분 실패 {recentFails[ip]}회</li>
            ))}
          </ul>
        </div>
      )}

      <div className="ad-card">
        <h3 className="ad-card-h">주소별 집계</h3>
        {intruders.length > 0 ? (
          <p className="admin-error">
            한 번도 성공하지 못한 주소가 {intruders.length}개 있습니다 — 남이 시도한 흔적일 수 있습니다.
          </p>
        ) : (
          <p className="ad-card-sub">성공 기록이 없는 주소는 없습니다. 외부 시도 흔적이 없다는 뜻입니다.</p>
        )}
        <div className="ad-table-wrap"><table className="admin-table">
          <thead>
            <tr><th>접속 주소</th><th>성공</th><th>실패</th><th>처음</th><th>마지막</th></tr>
          </thead>
          <tbody>
            {byIp.map((r) => {
              const alien = Number(r.OKCNT) === 0 && Number(r.NGCNT) > 0;
              return (
                <tr key={r.IP} className={alien ? 'ad-row-warn' : ''}>
                  <td>
                    {r.IP || '-'}
                    {alien && <span className="ad-tag warn">성공 없음</span>}
                  </td>
                  <td>{r.OKCNT}</td>
                  <td>{r.NGCNT}</td>
                  <td>{fmtDay(r.FIRST_AT)}</td>
                  <td>{fmt(r.LAST_AT)}</td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </div>

      <div className="ad-card">
        <h3 className="ad-card-h">
          최근 시도 <span className="ad-hint">최대 200건 · 90일 보관</span>
        </h3>
        <div className="ad-table-wrap"><table className="admin-table">
          <thead>
            <tr><th>시각</th><th>아이디</th><th>접속 주소</th><th>결과</th></tr>
          </thead>
          <tbody>
            {attempts.length === 0 && <tr><td colSpan="4" className="ad-empty">없습니다.</td></tr>}
            {attempts.map((a, i) => (
              <tr key={i}>
                <td>{fmt(a.CREATED_AT)}</td>
                <td>{a.ID || '-'}</td>
                <td>{a.IP || '-'}</td>
                <td className={a.SUCCESS ? 'ad-ok' : 'ad-ng'}>{a.SUCCESS ? '성공' : '실패'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      <div className="ad-card-foot">
        <button type="button" className="admin-btn-sm" onClick={load}>새로 고치기</button>
      </div>
    </>
  );
}

export default AdminAccess;
